import { useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Settings as SettingsIcon, 
  Moon, 
  Sun, 
  Globe, 
  DollarSign,
  Bell,
  Shield,
  Smartphone
} from 'lucide-react';

const SettingsPage = () => {
  const { theme, toggleTheme, isDark } = useTheme();
  const { language, setLanguage, languages, t } = useLanguage();
  const { currency, setCurrency, currencies } = useCurrency();
  const { updateProfile } = useAuth();
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true
  });

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    await updateProfile({ preferred_language: lang });
    toast.success('Language updated');
  };

  const handleCurrencyChange = async (curr) => {
    setCurrency(curr);
    await updateProfile({ preferred_currency: curr });
    toast.success('Currency updated');
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('settings')}</h1>
          <p className="text-muted-foreground mt-1">Manage your app preferences</p>
        </div>

        {/* Appearance */}
        <div className="card-clinical">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            {isDark ? <Moon className="w-5 h-5 text-sky-600" /> : <Sun className="w-5 h-5 text-sky-600" />}
            Appearance
          </h2>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-foreground">{isDark ? t('dark_mode') : t('light_mode')}</p>
              <p className="text-sm text-muted-foreground">Switch between light and dark themes</p>
            </div>
            <Switch 
              checked={isDark}
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle"
            />
          </div>
        </div>

        {/* Language */}
        <div className="card-clinical">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-sky-600" />
            {t('language')}
          </h2>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-foreground">Display Language</p>
              <p className="text-sm text-muted-foreground">Choose your preferred language</p>
            </div>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-48" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Currency */}
        <div className="card-clinical">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-sky-600" />
            {t('currency')}
          </h2>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-foreground">Display Currency</p>
              <p className="text-sm text-muted-foreground">Prices will be shown in this currency</p>
            </div>
            <Select value={currency} onValueChange={handleCurrencyChange}>
              <SelectTrigger className="w-48" data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    <span className="mr-2">{curr.symbol}</span>
                    {curr.name} ({curr.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Notifications */}
        <div className="card-clinical">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-sky-600" />
            Notifications
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-foreground">Email Notifications</p>
                <p className="text-sm text-muted-foreground">Receive booking and result updates via email</p>
              </div>
              <Switch 
                checked={notifications.email}
                onCheckedChange={(v) => setNotifications(prev => ({ ...prev, email: v }))}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-foreground">SMS Notifications</p>
                <p className="text-sm text-muted-foreground">Get text messages for important updates</p>
              </div>
              <Switch 
                checked={notifications.sms}
                onCheckedChange={(v) => setNotifications(prev => ({ ...prev, sms: v }))}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-foreground">Push Notifications</p>
                <p className="text-sm text-muted-foreground">Receive in-app notifications</p>
              </div>
              <Switch 
                checked={notifications.push}
                onCheckedChange={(v) => setNotifications(prev => ({ ...prev, push: v }))}
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="card-clinical">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-sky-600" />
            Security
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-foreground">Two-Factor Authentication</p>
                <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
              </div>
              <Button variant="outline" size="sm">Enable</Button>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-foreground">Change Password</p>
                <p className="text-sm text-muted-foreground">Update your account password</p>
              </div>
              <Button variant="outline" size="sm">Update</Button>
            </div>
          </div>
        </div>

        {/* App Info */}
        <div className="card-clinical">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-sky-600" />
            App Information
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Version</span>
              <span className="text-foreground">2.0.0</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Build</span>
              <span className="text-foreground">2024.01.30</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Platform</span>
              <span className="text-foreground">Web</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
